import { inject, Injectable } from '@angular/core';
import { RequestEntitiesService } from './request-entities.service';
import { RequestsApiService } from './requests-api.service';
import {
  ComparingLogs,
  RequestLog,
  RequestLogAction,
} from '@core/types/RequestLog';
import { createdAtAgo, Nullable, WithCreatedAgo } from '@core/helpers';
import { RequestDTO } from '@core/types';
import { map, Observable, of, switchMap, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RequestChangeHistoryService extends RequestEntitiesService<
  WithCreatedAgo<RequestLog>[]
> {
  private readonly requestsApiService = inject(RequestsApiService);
  private comparingLogs: Nullable<ComparingLogs> = null;

  readonly source$: (
    requestId: RequestDTO['id'],
  ) => Observable<WithCreatedAgo<RequestLog>[]> = (requestId: number) => {
    return this.requestsApiService.getRequestChangeHistory$(requestId).pipe(
      map(logs => {
        return logs.map(log => {
          return {
            ...log,
            createdAtAgo: log.createdAt ? createdAtAgo(log.createdAt) : '',
          };
        });
      }),
    );
  };

  public compareLogs(logId: RequestLog['id']): Observable<string | null> {
    return this.entities$.pipe(
      take(1),
      switchMap(logs => {
        this.comparingLogs = null;
        const editingLogs = logs
          .filter(log => {
            const isUpdateRequest =
              log.action === RequestLogAction.UPDATE &&
              log.entity === 'Заявки' &&
              log.context.startsWith('Изменил заявку');
            const isCreateRequest =
              log.action === RequestLogAction.CREATE && log.entity === 'Заявки';

            return isUpdateRequest || isCreateRequest;
          })
          .map(log => ({ id: log.id, context: log.context }));

        const currentLogPosition = editingLogs
          .map(log => log.id)
          .indexOf(logId);

        if (currentLogPosition < 0) {
          return of(null);
        }

        const currentLog = editingLogs[currentLogPosition + 1].context
          .split('\n')
          .slice(1, -1);
        const previousLog = editingLogs[currentLogPosition].context
          .split('\n')
          .slice(1, -1);

        const comparingLogs: ComparingLogs = {
          currentLog: currentLog.join('\n'),
          previousLog: previousLog.join('\n'),
        };

        this.comparingLogs = comparingLogs;

        return this.requestsApiService.compareRequestHistoryLogs$(
          comparingLogs,
        );
      }),
    );
  }

  public changeComparingLogsAlgorithm(changeAlgorithm: boolean) {
    if (!this.comparingLogs) {
      return of(null);
    }

    return this.requestsApiService.compareRequestHistoryLogs$(
      this.comparingLogs,
      changeAlgorithm,
    );
  }
}
